import { useState, useEffect, useContext } from 'react';
import { Search } from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';

interface Student {
  userId: string;
  name: string;
  grade?: string | number;
}

interface StudentSearchProps {
  onStudentSelect: (student: Student) => void;
}

export const StudentSearch = ({ onStudentSelect }: StudentSearchProps) => {
  const { userProfile } = useContext(UserContext);
  const roster: Student[] = (userProfile?.students || []) as unknown as Student[];
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Student[]>([]);

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase();
      const filteredStudents = roster.filter(student =>
        (student.name || '').toLowerCase().includes(query) ||
        (student.userId || '').toLowerCase().includes(query)
      );
      setSearchResults(filteredStudents);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, roster]);

  return (
    <div className="relative">
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or student ID..."
          className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        />
      </div>

      {searchResults.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {searchResults.map((student) => (
            <button
              key={student.userId}
              onClick={() => {
                onStudentSelect(student);
                setSearchQuery('');
                setSearchResults([]);
              }}
              className="w-full text-left px-4 py-2 hover:bg-gray-100"
            >
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium text-gray-900">{student.name}</span>
                  <span className="ml-2 text-sm text-gray-500">({student.userId})</span>
                </div>
                <span className="text-sm text-gray-500">{student.grade} Grade</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};